/* archive-engine.js — main-thread client for the 7z-wasm archive worker.
 *
 * Lazy: the worker (and the ~1.65 MB WASM it fetches) is only spun up on the
 * first open(). Promise-based RPC; extractAll streams entries back so the UI
 * can write each to disk (File System Access) or into a streaming zip without
 * ever holding the whole extracted payload in memory.
 *
 * Public: window.ArchiveEngine.create() -> instance, plus static helpers
 * (isArchiveName, magicIsArchive, decodeName, nameEntries, CODEPAGES).
 */
(function () {
  'use strict';
  if (window.ArchiveEngine) return;

  function vh(key) {
    var h = (window.__VENDOR_HASHES || {})[key];
    return h ? ('?v=' + h) : '';
  }
  /* Server-resolved: the blade emits __ARCHIVE_WORKER_URL via AssetRouter::worker(),
   * which picks the minified twin and appends ?v=filemtime. That version query is
   * not cosmetic — `new Worker()` does not go through the Service Worker, so a
   * hardcoded URL keeps serving pre-fix worker bytes from the browser's own HTTP
   * cache for up to 24 h after a deploy. Literal path stays as the dev fallback. */
  /* __ARCHIVE_ENGINE_WORKER_URL wins over the legacy shared global: engine and
   * builder used to read the SAME __ARCHIVE_WORKER_URL with different defaults,
   * so a page that both reads and writes archives (file-converter) could not
   * set it correctly for either. Legacy single-role blades keep working. */
  var WORKER_URL = window.__ARCHIVE_ENGINE_WORKER_URL || window.__ARCHIVE_WORKER_URL || '/js/archive/sevenzip-worker.js';
  var JS_URL = '/vendor/7z-wasm/7zz.es6.js' + vh('sevenZipJs');
  var WASM_URL = '/vendor/7z-wasm/7zz.wasm' + vh('sevenZipWasm');

  // extensions we treat as archives (for nested "open inside" + accept filter)
  var ARCHIVE_EXTS = ['zip', '7z', 'rar', 'tar', 'gz', 'tgz', 'bz2', 'tbz2', 'tbz',
    'xz', 'txz', 'zst', 'tzst', 'zstd', 'cab', 'iso', 'cpio', 'lzh', 'lha', 'lzma',
    'lz', 'lz4', 'arj', 'wim', 'swm', 'rpm', 'deb', 'ar', 'a', 'xar', 'pkg', 'dmg',
    'wbm', 'z', 'taz', 'apk', 'jar', 'war', 'crx', 'whl', 'nupkg', 'vhd', 'vhdx',
    'vmdk', 'vdi', 'qcow2', 'squashfs', 'cramfs', 'chm', 'msi', 'udf', 'gpt', 'mbr',
    'fat', 'ntfs', 'hfs', 'ext', 'esd'];

  function ext(name) {
    var m = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/);
    return m ? m[1] : '';
  }
  function isArchiveName(name) { return ARCHIVE_EXTS.indexOf(ext(name)) !== -1; }

  /* Duck-typed on purpose. `x instanceof Blob` compares against THIS window's
   * Blob constructor, so a File handed over from another tab (the /archive-creator
   * → extractor handoff opens a new window and passes the File through
   * window.opener) fails the check, gets treated as raw bytes, and dies on the
   * missing .buffer — the archive simply never opened. Tag + shape survive the
   * realm boundary. */
  function isFileLike(x) {
    if (!x || typeof x !== 'object') return false;
    var tag = Object.prototype.toString.call(x);
    if (tag === '[object File]' || tag === '[object Blob]') return true;
    return typeof x.size === 'number' && typeof x.slice === 'function' && typeof x.arrayBuffer === 'function';
  }

  /* Split archives are named `thing.7z.001`, `.002`, … 7-Zip follows the chain
   * on its own, but ONLY from the first part and ONLY if the siblings are
   * reachable. Telemetry: 13 of the rejected uploads in 30 days were someone
   * handing us part 2 or 3 and being told "this tool works with a suitable
   * file", which explains nothing. */
  function volumeInfo(name) {
    var m = String(name || '').match(/^(.*)\.(\d{3,})$/);
    if (!m) return null;
    return { base: m[1], index: parseInt(m[2], 10), width: m[2].length };
  }

  // Given everything the user picked, work out which one to open and which are
  // its siblings. Returns null when the selection is not a split archive.
  function volumeSet(files) {
    var list = Array.prototype.slice.call(files || []);
    var infos = list.map(function (f) { return { file: f, vi: volumeInfo(f.name) }; });
    var vols = infos.filter(function (x) { return x.vi; });
    if (!vols.length) return null;
    var base = vols[0].vi.base;
    var same = vols.filter(function (x) { return x.vi.base === base; });
    same.sort(function (a, b) { return a.vi.index - b.vi.index; });
    var first = same.filter(function (x) { return x.vi.index === 1; })[0];
    return {
      base: base,
      parts: same.map(function (x) { return x.file; }),
      first: first ? first.file : null,
      lowest: same[0].vi.index,
      count: same.length,
    };
  }

  // magic-byte sniff (first bytes) for the common containers
  function magicIsArchive(bytes) {
    if (!bytes || bytes.length < 4) return false;
    var b = bytes;
    var sig = function (arr, off) { off = off || 0; for (var i = 0; i < arr.length; i++) if (b[off + i] !== arr[i]) return false; return true; };
    if (sig([0x50, 0x4B])) return true;                              // PK.. zip/jar/apk/...
    if (sig([0x37, 0x7A, 0xBC, 0xAF, 0x27, 0x1C])) return true;      // 7z
    if (sig([0x52, 0x61, 0x72, 0x21])) return true;                  // Rar!
    if (sig([0x1F, 0x8B])) return true;                              // gzip
    if (sig([0x42, 0x5A, 0x68])) return true;                        // BZh bzip2
    if (sig([0xFD, 0x37, 0x7A, 0x58, 0x5A])) return true;            // xz
    if (sig([0x28, 0xB5, 0x2F, 0xFD])) return true;                  // zstd
    if (sig([0x04, 0x22, 0x4D, 0x18])) return true;                  // lz4
    if (sig([0x4D, 0x53, 0x43, 0x46])) return true;                  // MSCF cab
    if (b.length > 0x8001 && sig([0x43, 0x44, 0x30, 0x30, 0x31], 0x8001)) return true; // CD001 iso
    if (sig([0x21, 0x3C, 0x61, 0x72, 0x63, 0x68])) return true;      // !<arch> ar/deb
    if (b.length > 0x101 && sig([0x75, 0x73, 0x74, 0x61, 0x72], 0x101)) return true;   // ustar tar
    return false;
  }

  // single-stream compressor container types (7-Zip `Type`) that wrap exactly one file
  function isCompressorType(type) { return /^(gzip|bzip2|xz|zstd|z|lzma|lzma86|lz4|lzip|lzh?)$/i.test(String(type || '').trim()); }
  function onlyFiles(arr) { return (arr || []).filter(function (e) { return !e.isDir; }); }

  /* ---------- entry names: the KEY 7-Zip matches vs. the NAME a person reads ----------
   *
   * The worker hands back every name exactly as the archive stores it: bytes,
   * carried through the patched glue as a string in which valid UTF-8 reads as
   * itself and any other byte b is the code unit U+DC00+b
   * (scripts/sevenzip-glue-patch.mjs). That string is the entry's KEY — the only
   * spelling extraction will ever match — and it never leaves this file.
   *
   * What the bytes SAY is a question 7-Zip cannot answer in the browser. A ZIP
   * made by Windows Explorer stores names in the OEM code page — CP866 on Russian
   * Windows, 437/850 on Western ones — with no flag saying which, and this
   * (Linux) build of 7-Zip ignores -mcp=. Desktop unzippers get it right only
   * because they ask the OS. Here the bytes are read under each candidate code
   * page and the reading that looks like WORDS wins: letters, one script per
   * word, no lower→Upper flip inside a word, no box-drawing or invalid sequences.
   * The page's language only breaks near-ties (Russian: 866, then 1251). The
   * encoding picker overrides all of it.
   *
   * Comment #1857 (2026-10-08): a 332 MB zip with 52 Cyrillic-named files opened
   * fine and every single preview failed — the name went out as U+FFFD and came
   * back matching nothing. 71 sessions in 20 days had the same `preview /
   * extract_failed`, most of them on small zips. */
  var DOS_UPPER = {
    437: '\u00c7\u00fc\u00e9\u00e2\u00e4\u00e0\u00e5\u00e7\u00ea\u00eb\u00e8\u00ef\u00ee\u00ec\u00c4\u00c5\u00c9\u00e6\u00c6\u00f4\u00f6\u00f2\u00fb\u00f9\u00ff\u00d6\u00dc\u00a2\u00a3\u00a5\u20a7\u0192\u00e1\u00ed\u00f3\u00fa\u00f1\u00d1\u00aa\u00ba\u00bf\u2310\u00ac\u00bd\u00bc\u00a1\u00ab\u00bb\u2591\u2592\u2593\u2502\u2524\u2561\u2562\u2556\u2555\u2563\u2551\u2557\u255d\u255c\u255b\u2510\u2514\u2534\u252c\u251c\u2500\u253c\u255e\u255f\u255a\u2554\u2569\u2566\u2560\u2550\u256c\u2567\u2568\u2564\u2565\u2559\u2558\u2552\u2553\u256b\u256a\u2518\u250c\u2588\u2584\u258c\u2590\u2580\u03b1\u00df\u0393\u03c0\u03a3\u03c3\u00b5\u03c4\u03a6\u0398\u03a9\u03b4\u221e\u03c6\u03b5\u2229\u2261\u00b1\u2265\u2264\u2320\u2321\u00f7\u2248\u00b0\u2219\u00b7\u221a\u207f\u00b2\u25a0\u00a0',
    850: '\u00c7\u00fc\u00e9\u00e2\u00e4\u00e0\u00e5\u00e7\u00ea\u00eb\u00e8\u00ef\u00ee\u00ec\u00c4\u00c5\u00c9\u00e6\u00c6\u00f4\u00f6\u00f2\u00fb\u00f9\u00ff\u00d6\u00dc\u00f8\u00a3\u00d8\u00d7\u0192\u00e1\u00ed\u00f3\u00fa\u00f1\u00d1\u00aa\u00ba\u00bf\u00ae\u00ac\u00bd\u00bc\u00a1\u00ab\u00bb\u2591\u2592\u2593\u2502\u2524\u00c1\u00c2\u00c0\u00a9\u2563\u2551\u2557\u255d\u00a2\u00a5\u2510\u2514\u2534\u252c\u251c\u2500\u253c\u00e3\u00c3\u255a\u2554\u2569\u2566\u2560\u2550\u256c\u00a4\u00f0\u00d0\u00ca\u00cb\u00c8\u0131\u00cd\u00ce\u00cf\u2518\u250c\u2588\u2584\u00a6\u00cc\u2580\u00d3\u00df\u00d4\u00d2\u00f5\u00d5\u00b5\u00fe\u00de\u00da\u00db\u00d9\u00fd\u00dd\u00af\u00b4\u00ad\u00b1\u2017\u00be\u00b6\u00a7\u00f7\u00b8\u00b0\u00a8\u00b7\u00b9\u00b3\u00b2\u25a0\u00a0',
    720: '\u0080\u0081\u00e9\u00e2\u0084\u00e0\u0086\u00e7\u00ea\u00eb\u00e8\u00ef\u00ee\u008d\u008e\u008f\u0090\u0651\u0652\u00f4\u00a4\u0640\u00fb\u00f9\u0621\u0622\u0623\u0624\u00a3\u0625\u0626\u0627\u0628\u0629\u062a\u062b\u062c\u062d\u062e\u062f\u0630\u0631\u0632\u0633\u0634\u0635\u00ab\u00bb\u2591\u2592\u2593\u2502\u2524\u2561\u2562\u2556\u2555\u2563\u2551\u2557\u255d\u255c\u255b\u2510\u2514\u2534\u252c\u251c\u2500\u253c\u255e\u255f\u255a\u2554\u2569\u2566\u2560\u2550\u256c\u2567\u2568\u2564\u2565\u2559\u2558\u2552\u2553\u256b\u256a\u2518\u250c\u2588\u2584\u258c\u2590\u2580\u0636\u0637\u0638\u0639\u063a\u0641\u00b5\u0642\u0643\u0644\u0645\u0646\u0647\u0648\u0649\u064a\u2261\u064b\u064c\u064d\u064e\u064f\u0650\u2248\u00b0\u2219\u00b7\u221a\u207f\u00b2\u25a0\u00a0',
  };
  // TextDecoder (WHATWG) labels; DOS pages it lacks come from the tables above
  // (generated from Python's cp437/cp850/cp720 codecs, not typed by hand).
  var TD_LABEL = { 866: 'ibm866', 1251: 'windows-1251', 1252: 'windows-1252', 1250: 'windows-1250',
    1256: 'windows-1256', 936: 'gbk', 950: 'big5', 932: 'shift_jis', 949: 'euc-kr' };
  var MULTIBYTE = { 936: 1, 950: 1, 932: 1, 949: 1 };

  var CODEPAGES = [
    { id: 0, label: 'Auto' },
    { id: 866, label: 'CP866 (Cyrillic DOS)' },
    { id: 1251, label: 'Windows-1251 (Cyrillic)' },
    { id: 437, label: 'CP437 (US DOS)' },
    { id: 850, label: 'CP850 (Western DOS)' },
    { id: 1252, label: 'Windows-1252 (Western)' },
    { id: 1250, label: 'Windows-1250 (Central European)' },
    { id: 720, label: 'CP720 (Arabic DOS)' },
    { id: 1256, label: 'Windows-1256 (Arabic)' },
    { id: 949, label: 'EUC-KR (Korean)' },
    { id: 936, label: 'GBK (Simplified Chinese)' },
    { id: 950, label: 'Big5 (Traditional Chinese)' },
    { id: 932, label: 'Shift-JIS (Japanese)' },
  ];
  /* Auto never guesses a CJK page outside its own language: GBK reads almost any
   * byte pair as some Han character, so on a CP866 name it "succeeds" with
   * nonsense. Single-byte pages are always in the running. */
  var AUTO_CANDIDATES = [866, 1251, 437, 850, 1252, 1250, 720, 1256];
  var LOCALE_PRIOR = {
    ru: [866, 1251], uk: [866, 1251], ko: [949], ar: [720, 1256],
    de: [850, 1252, 437], fr: [850, 1252, 437], es: [850, 1252, 437], pt: [850, 1252, 437],
    en: [437, 850, 1252], id: [437, 850, 1252],
  };

  function isEscape(s, i) {
    var c = s.charCodeAt(i);
    if (c < 0xDC80 || c > 0xDCFF) return false;
    var p = i > 0 ? s.charCodeAt(i - 1) : 0;
    return !(p >= 0xD800 && p <= 0xDBFF);            // the low half of a real pair is not an escape
  }
  function hasRawBytes(key) {
    for (var i = 0; i < key.length; i++) if (isEscape(key, i)) return true;
    return false;
  }
  function isAscii(s) { for (var i = 0; i < s.length; i++) if (s.charCodeAt(i) > 127) return false; return true; }

  // KEY → the archive's bytes. Mirror of the glue's encoder, byte for byte.
  function keyBytes(key) {
    var out = [];
    for (var i = 0; i < key.length; i++) {
      var c = key.charCodeAt(i);
      if (isEscape(key, i)) { out.push(c - 0xDC00); continue; }
      if (c >= 0xD800 && c <= 0xDBFF && i + 1 < key.length) {
        var d = key.charCodeAt(i + 1);
        if (d >= 0xDC00 && d <= 0xDFFF) { c = 0x10000 + ((c - 0xD800) << 10) + (d - 0xDC00); i++; }
      }
      if (c < 0x80) out.push(c);
      else if (c < 0x800) out.push(0xC0 | c >> 6, 0x80 | c & 63);
      else if (c < 0x10000) out.push(0xE0 | c >> 12, 0x80 | c >> 6 & 63, 0x80 | c & 63);
      else out.push(0xF0 | c >> 18, 0x80 | c >> 12 & 63, 0x80 | c >> 6 & 63, 0x80 | c & 63);
    }
    return new Uint8Array(out);
  }

  var tdCache = {};
  function decodeBytes(bytes, cp) {
    var table = DOS_UPPER[cp];
    if (table) {
      var s = '';
      for (var i = 0; i < bytes.length; i++) s += bytes[i] < 128 ? String.fromCharCode(bytes[i]) : table.charAt(bytes[i] - 128);
      return s;
    }
    var label = TD_LABEL[cp];
    if (!label || typeof TextDecoder === 'undefined') return null;
    try { return (tdCache[cp] || (tdCache[cp] = new TextDecoder(label))).decode(bytes); }
    catch (e) { return null; }                          // a label this browser does not know
  }

  function scriptOf(c) {
    if (c < 0x250) return 'Latn';
    if (c >= 0x370 && c < 0x400) return 'Grek';
    if (c >= 0x400 && c < 0x530) return 'Cyrl';
    if (c >= 0x590 && c < 0x600) return 'Hebr';
    if ((c >= 0x600 && c < 0x700) || (c >= 0xFB50 && c < 0xFE00) || (c >= 0xFE70 && c < 0xFF00)) return 'Arab';
    if ((c >= 0x1100 && c < 0x1200) || (c >= 0x3130 && c < 0x3190) || (c >= 0xAC00 && c < 0xD7B0)) return 'Hang';
    // kana and Han share words in Japanese — one group, so 漢字かな is not a "flip"
    if ((c >= 0x3040 && c < 0x3100) || (c >= 0x3400 && c < 0x4DC0) || (c >= 0x4E00 && c < 0xA000) || (c >= 0xF900 && c < 0xFB00)) return 'CJK';
    return 'Othr';
  }
  // Punctuation real file names carry. Wrong readings produce it too, so it costs
  // half a symbol rather than nothing.
  var NAME_PUNCT = '\u2116\u00ab\u00bb\u2013\u2014\u2018\u2019\u201c\u201d\u2026\u00a0\u00a9\u00ae';

  /* How much a decoded string reads like file names: per non-ASCII BYTE, +1 for a
   * letter, −2 for each sign of a wrong code page. ASCII reads the same under
   * every candidate, so it only supplies the word a flip is judged inside. */
  function plausibility(s, multibyte) {
    var good = 0, bad = 0, nonAsciiBytes = 0, prevScript = '', prevLower = false;
    var w = multibyte ? 2 : 1;
    for (var i = 0; i < s.length; i++) {
      var c = s.charCodeAt(i);
      var ch = s.charAt(i);
      if (c >= 0xD800 && c <= 0xDFFF) {                // astral or a stray surrogate: no business in a name
        if (c <= 0xDBFF && i + 1 < s.length) i++;
        nonAsciiBytes += w; bad += 2; prevScript = ''; prevLower = false; continue;
      }
      var lower = ch !== ch.toUpperCase(), upper = ch !== ch.toLowerCase();
      var sc = scriptOf(c);
      var letter = lower || upper || (c >= 0x590 && sc !== 'Othr' && sc !== 'Latn');
      if (c < 128) {
        if (letter) { if (prevScript && prevScript !== 'Latn') bad += 1; prevScript = 'Latn'; prevLower = lower; }
        else { prevScript = ''; prevLower = false; }
        continue;
      }
      nonAsciiBytes += c < 0x100 ? 1 : w;
      if (c === 0xFFFD || c < 0xA0) { bad += 2; prevScript = ''; prevLower = false; continue; }
      if (!letter) {
        bad += NAME_PUNCT.indexOf(ch) >= 0 ? 0.5 : 1;
        prevScript = ''; prevLower = false;
        continue;
      }
      if (c >= 0xFF61 && c <= 0xFF9F) bad += 1;     // half-width katakana: what other pages' bytes become under Shift-JIS
      good += c < 0x100 ? 1 : w;
      if (prevScript && prevScript !== sc) bad += 1;  // script flip inside one word
      if (prevLower && upper) bad += 1;               // «ЋвзсвЂ»: lower → Upper inside one word
      prevScript = sc; prevLower = lower;
    }
    if (!nonAsciiBytes) return 1;
    return (good - 2 * bad) / nonAsciiBytes;
  }

  function pageLang() {
    var l = (typeof window !== 'undefined' && window.__LOCALE) ||
      (typeof document !== 'undefined' && document.documentElement && document.documentElement.lang) || 'en';
    return String(l).slice(0, 2).toLowerCase();
  }

  /* Best code page for an archive's non-UTF-8 names, read all together — one
   * archive was written by one program with one code page. */
  function detectCodepage(byteList, lang) {
    var prior = LOCALE_PRIOR[lang] || LOCALE_PRIOR.en;
    var cands = prior.concat(AUTO_CANDIDATES.filter(function (cp) { return prior.indexOf(cp) < 0; }));
    var total = 0;
    for (var i = 0; i < byteList.length; i++) total += byteList[i].length + 1;
    var joined = new Uint8Array(total), o = 0;
    for (var j = 0; j < byteList.length; j++) { joined.set(byteList[j], o); o += byteList[j].length; joined[o++] = 0x2F; }
    var best = 0, bestScore = -Infinity;
    for (var k = 0; k < cands.length; k++) {
      var s = decodeBytes(joined, cands[k]);
      if (s == null) continue;
      // a near-tie goes to the page's language, its first page first
      var score = plausibility(s, !!MULTIBYTE[cands[k]]) + (k < prior.length ? 0.05 - 0.02 * k : 0);
      if (score > bestScore) { bestScore = score; best = cands[k]; }
    }
    return best;
  }

  /* The name a person reads, for one KEY. `cp` 0 = this archive has no legacy
   * names; `forced` = the person picked the code page themselves. */
  function decodeName(key, cp, forced) {
    if (!key) return key;
    var name = key;
    if (cp && !isAscii(key)) {
      if (forced || hasRawBytes(key)) {
        var d = decodeBytes(keyBytes(key), cp);
        if (d != null) name = d;
      } else {
        // Valid UTF-8 inside a legacy archive: usually a real UTF-8 name, now and
        // then CP866 bytes that happen to form UTF-8 (у+м+е = E3 AC A5 = 㬥). Keep
        // the reading that looks more like words; a tie stays UTF-8.
        var alt = decodeBytes(keyBytes(key), cp);
        if (alt != null && plausibility(alt, !!MULTIBYTE[cp]) > plausibility(key, false)) name = alt;
      }
    }
    // never hand a lone surrogate onwards (file systems refuse it, encoders throw)
    if (hasRawBytes(name)) name = name.replace(/[\uDC80-\uDCFF]/g, '\uFFFD');
    // Windows tools write `dir\file`; this build of 7-Zip keeps it as one name.
    return name.replace(/\\/g, '/');
  }

  /* Two keys, one reading (an undecodable byte became U+FFFD in both): both stay
   * reachable, the second under a numbered name, «a (2).txt». */
  function uniqueName(p, key, used) {
    if (!Object.prototype.hasOwnProperty.call(used, p) || used[p] === key) return p;
    var dot = p.lastIndexOf('.'), slash = p.lastIndexOf('/');
    var stem = dot > slash + 1 ? p.slice(0, dot) : p, tail = dot > slash + 1 ? p.slice(dot) : '';
    var n = 2;
    while (Object.prototype.hasOwnProperty.call(used, stem + ' (' + n + ')' + tail)) n++;
    return stem + ' (' + n + ')' + tail;
  }

  /* Give every entry its display `path` and keep its `key`. Returns the map the
   * engine translates back with, and what the UI shows about the choice. */
  function nameEntries(entries, codepage, lang) {
    var legacyBytes = [];
    for (var i = 0; i < entries.length; i++) {
      var k = entries[i].path || '';
      if (hasRawBytes(k)) legacyBytes.push(keyBytes(k));
    }
    var forced = !!codepage;
    var cp = forced ? codepage : (legacyBytes.length ? detectCodepage(legacyBytes, lang || pageLang()) : 0);
    var keyByPath = {}, used = {};
    for (var j = 0; j < entries.length; j++) {
      var e = entries[j];
      var key = e.path || '';
      var p = decodeName(key, cp, forced);
      if (!e.isDir) p = uniqueName(p, key, used);
      used[p] = key;
      e.key = key;
      e.path = p;
      keyByPath[p] = key;
    }
    return { keyByPath: keyByPath, codepage: cp, legacy: legacyBytes.length > 0, forced: forced };
  }

  /* The same, for names that arrive one at a time (/archive-repair streams
   * entries as each strategy recovers them). The code page is decided by the
   * first name that is not UTF-8 and then held for the whole job — one archive,
   * one writer, one page — so a folder's name cannot change between its files. */
  function nameReader(lang, codepage) {
    var cp = codepage || 0, decided = !!codepage, byKey = {}, used = {};
    return {
      read: function (key) {
        key = String(key || '');
        if (Object.prototype.hasOwnProperty.call(byKey, key)) return byKey[key];
        if (!decided && hasRawBytes(key)) { cp = detectCodepage([keyBytes(key)], lang || pageLang()); decided = true; }
        var p = uniqueName(decodeName(key, cp, !!codepage), key, used);
        used[p] = key; byKey[key] = p;
        return p;
      },
      codepage: function () { return cp; },
    };
  }

  function Engine() {
    this._worker = null;
    this._seq = 0;
    // Контракт WorkerWatchdog.getPending — Map: подделка с size/forEach без .get
    // падала в ветке «воркер жив, задача зависла» (TIMBRICA-FRONTEND-HT).
    this._pending = new Map();
    this._opened = false;
    this._watchdog = null;
    this.onProgress = null;   // set by the UI: {op, pos, total, bytes}
  }

  Engine.prototype._ensureWorker = function () {
    if (this._worker) return;
    var self = this;
    /* Firefox 68 (Sentry HG/HF) модульный воркер НЕ отвергает: он стартует и
       падает на первом `import` — SyntaxError в window.onerror, а человеку
       «worker_crash». Спрашиваем движок ДО создания; общий пробник в layout
       сам говорит человеку и пишет телеметрию, нам остаётся честно остановиться. */
    if (typeof window !== 'undefined' && window.__requireModuleWorker && !window.__requireModuleWorker()) {
      var tooOld = new Error('module_worker_unsupported'); tooOld.code = 'MODULE_WORKER_UNSUPPORTED';
      // Та же фраза, что сказал слой, — для страниц, которые пишут userMessage в строку файла (/file-converter).
      tooOld.userMessage = window.__requireModuleWorker.text || ''; throw tooOld;
    }
    this._worker = new Worker(WORKER_URL, { type: 'module' });
    this._worker.onmessage = function (ev) {
      var d = ev.data || {};
      /* Liveness/progress tick, not a reply: the operation is still running.
       * Keep the pending entry, refresh the watchdog, tell the caller. */
      if (d.progress) {
        if (self._watchdog) self._watchdog.touch(d.id);
        if (typeof self.onProgress === 'function') {
          try { self.onProgress({ op: d.op, pos: d.pos || 0, total: d.total || 0, bytes: d.bytes || 0 }); } catch (e) {}
        }
        return;
      }
      var p = self._pending.get(d.id);
      if (!p) return;
      self._pending.delete(d.id);
      if (self._watchdog) self._watchdog.forget(d.id);
      if (d.ok) p.resolve(d.result);
      else { var e = new Error((d.error && d.error.message) || 'worker_error'); e.code = d.error && d.error.code; e.why = (d.error && d.error.why) || ''; p.reject(e); }
    };
    this._worker.onerror = function (e) {
      // The tool takes the failure (every pending call rejects with worker_crash and the page
      // says so); uncancelled it would also reach window.onerror — the shared toast, Sentry.
      if (e && e.preventDefault) e.preventDefault();
      self._pending.forEach(function (p) { try { p.reject(new Error('worker_crash: ' + (e.message || ''))); } catch (_) {} });
      self._pending.clear();
    };
    /* A zombie worker (Android OOM kills the runtime but leaves the handle
     * alive) is indistinguishable from a slow one from the page's side, and
     * before the progress ticks above there was no way to tell them apart at
     * all — the tool simply hung forever with no error and no telemetry.
     * The timeout counts silence, not duration: a legitimate multi-minute
     * extract ticks several times a second, so only a genuinely dead runtime
     * trips it. Shared primitive — same one the mp3 encoder uses. */
    if (window.WorkerWatchdog && !this._watchdog) {
      this._watchdog = window.WorkerWatchdog.create({
        name: 'archive',
        getPending: function () { return self._pending; },
        terminateWorker: function () { try { if (self._worker) self._worker.terminate(); } catch (e) {} self._worker = null; },
        getHeartbeatTimeoutMs: function () { return Number(window.__ARCHIVE_WORKER_TIMEOUT_OVERRIDE) || 120000; },
        getTickMs: function () { return Number(window.__ARCHIVE_WATCHDOG_TICK_OVERRIDE) || 5000; },
      });
    }
  };

  Engine.prototype._rpc = function (op, payload, transfer) {
    this._ensureWorker();
    var self = this;
    var id = ++this._seq;
    return new Promise(function (resolve, reject) {
      self._pending.set(id, { resolve: resolve, reject: reject });
      if (self._watchdog) self._watchdog.mark(id);
      var msg = Object.assign({ id: id, op: op }, payload);
      self._worker.postMessage(msg, transfer || []);
    });
  };

  /**
   * Open an archive. `file` is a File/Blob/ArrayBuffer/Uint8Array.
   * Returns {type, headerEncrypted, solid, entries[], needsPassword,
   *          codepage, legacyNames}.
   * entries: {path, size, packed, isDir, encrypted, modified, crc, method}
   * `path` is the name a person reads (code page decided, `/` separators); every
   * method of this engine takes that same `path` back. `codepage` 0 = Auto.
   */
  Engine.prototype.open = async function (file, opts) {
    opts = opts || {};
    this._password = opts.password || '';
    this._codepage = opts.codepage || 0;

    var res;
    if (isFileLike(file)) {
      /* Zero-copy path: hand the File handle(s) to the worker and let WORKERFS
       * read them off disk. Reading the whole archive into an ArrayBuffer first
       * is what forced the size cap that rejected 332 uploads in 30 days.
       * `opts.parts` carries the sibling volumes of a split archive so 7-Zip can
       * follow .001 → .002 → … itself. */
      var parts = (opts.parts && opts.parts.length) ? opts.parts : [file];
      var openName = (file && file.name) || 'archive';
      this._name = openName;
      this._files = parts;
      res = await this._rpc('open', {
        name: openName,
        files: parts.map(function (f) { return { name: f.name, file: f }; }),
        password: this._password, jsUrl: JS_URL, wasmUrl: WASM_URL,
      });
    } else {
      // Nested archives arrive as extracted bytes — nothing on disk to mount.
      var buf;
      if (file instanceof ArrayBuffer) buf = file;
      else buf = file.buffer.slice(file.byteOffset, file.byteOffset + file.byteLength);
      this._name = 'archive';
      this._files = null;
      res = await this._rpc('open', { name: 'archive', bytes: buf, password: this._password, jsUrl: JS_URL, wasmUrl: WASM_URL }, [buf]);
    }
    this._adoptNames(res);
    // Auto-descend single-stream compressors (.gz/.xz/.bz2/.zst/.lz4/.lzma/.Z
    // wrap exactly one file). 7-Zip unwraps the filter one layer at a time,
    // surfacing an intermediate (e.g. a .tar) — keep descending so callers get
    // the REAL files, not a lone .tar. Verified byte-for-byte on tar.xz/tar.zst.
    var guard = 0;
    while (isCompressorType(res.type) && onlyFiles(res.entries).length === 1 && guard++ < 8) {
      var only = onlyFiles(res.entries)[0];
      var ex;
      try { ex = await this._rpc('extractEntry', { path: this._keyOf(only.path), password: this._password }); }
      catch (e) { break; }
      if (!ex || !ex.bytes) break;
      var innerName = only.path.split('/').pop() || 'archive';
      // ⚠️ Only descend when the payload really IS another archive. `open` REPLACES
      // the worker's mounted archive, and a plain payload (note.txt inside
      // note.txt.gz) leaves the worker mounted on a non-archive while `res` still
      // describes the .gz — every later extractEntry then fails with
      // `extract_failed`. MEASURED 2026-07-29 on a one-file gzip: entries showed
      // «note.txt», the row appeared TWICE in the tree, preview said «Не удалось
      // распаковать этот файл», and «Распаковать всё» produced no file at all —
      // while GZ/BZ2/XZ are advertised formats. Checking before touching the
      // worker keeps single-file compressors extractable, and tar.gz/tar.xz still
      // descend because a .tar payload passes both predicates.
      if (!isArchiveName(innerName) && !magicIsArchive(ex.bytes)) break;
      var innerBuf = ex.bytes.buffer;
      var inner = await this._rpc('open', { name: innerName, bytes: innerBuf, password: this._password, jsUrl: JS_URL, wasmUrl: WASM_URL }, [innerBuf]);
      if (!inner.entries || !inner.entries.length) break;
      this._adoptNames(inner);
      res = inner;
    }
    this._opened = true;
    this._solid = !!res.solid;
    this._sizeByPath = {};
    var es = res.entries || [];
    for (var k = 0; k < es.length; k++) if (!es[k].isDir) this._sizeByPath[es[k].path] = es[k].size || 0;
    return res;
  };

  /* `reopen(file, opts)` used to live here as a pure alias for open(). Nothing
   * called it (checked across public/js and resources/views), and a second name
   * for the same door is where "which one re-mounts?" confusion starts. Re-open
   * with a new password/codepage IS open() — the UI passes the same File back. */

  /* Every listing the worker returns gets its names decoded here, once, and the
   * engine keeps the way back. Display path → KEY is the only translation the
   * worker ever needs; callers never see a key. */
  Engine.prototype._adoptNames = function (res) {
    var named = nameEntries((res && res.entries) || [], this._codepage, pageLang());
    this._keyByPath = named.keyByPath;
    if (res) { res.codepage = named.codepage; res.legacyNames = named.legacy; }
  };
  Engine.prototype._keyOf = function (path) {
    var m = this._keyByPath || {};
    return Object.prototype.hasOwnProperty.call(m, path) ? m[path] : path;
  };

  /** Extract a single entry -> Uint8Array (preview / single download). */
  Engine.prototype.extractEntry = async function (path) {
    var r = await this._rpc('extractEntry', { path: this._keyOf(path), password: this._password, expectedSize: (this._sizeByPath || {})[path] || 0 });
    return r.bytes;
  };

  /**
   * Extract many entries, invoking `await onEntry({path, bytes})` for each.
   * PULL-BASED + backpressured: the next entry is only extracted after the
   * consumer's onEntry (e.g. a File System Access write) resolves, so memory
   * stays bounded to one entry and there is no producer/consumer race.
   * Non-solid: one cheap single-file extract per entry. Solid: decode the
   * whole block once, then drain files out of MEMFS one at a time.
   */
  /* One decode pass per BATCH, sized by how much decoded data we are willing to
   * hold in the worker's MEMFS at once.
   *
   * The old rule was "solid → one pass, otherwise one 7-Zip run per file", and
   * it cost 74 ms per file: a real 8039-entry archive took 10 minutes, which is
   * why big archives open far more often than they finish (telemetry: >4 GB —
   * 43 sessions opened, 8 completed). Batching is not a solid/non-solid
   * question, it is a memory question, so that is what decides it here.
   *
   * The budget also fixes the mirror-image bug: the solid path used to decode
   * the WHOLE archive in one go regardless of size, which on a multi-GB archive
   * simply exhausts the 32-bit wasm heap. Now an oversized set falls back to
   * per-entry — slow, but bounded and it finishes. */
  var BATCH_BUDGET_BYTES = 384 * 1024 * 1024;

  Engine.prototype.extractAll = async function (paths, onEntry) {
    var sizes = this._sizeByPath || {};
    var self = this;
    /* Entries 7-Zip did not produce (a damaged member, a method it lacks). They
     * used to vanish without a word — the run reported success with fewer files.
     * Returned so a caller can say so; callers that do not care ignore it. */
    var missing = [];

    async function drain(batch) {
      try {
        for (var i = 0; i < batch.length; i++) {
          var r = await self._rpc('readExtracted', { path: self._keyOf(batch[i]), expectedSize: sizes[batch[i]] || 0 });
          if (r && r.bytes) await onEntry({ path: batch[i], bytes: r.bytes });
          else missing.push(batch[i]);
        }
      } finally { await self._rpc('extractSolidEnd', {}); }
    }

    // Split into batches that each stay under the budget. A single entry larger
    // than the budget still gets its own batch — one file has to fit somewhere.
    var batches = [], cur = [], curBytes = 0;
    for (var i = 0; i < paths.length; i++) {
      var sz = sizes[paths[i]] || 0;
      if (cur.length && curBytes + sz > BATCH_BUDGET_BYTES) { batches.push(cur); cur = []; curBytes = 0; }
      cur.push(paths[i]); curBytes += sz;
    }
    if (cur.length) batches.push(cur);

    /* A solid archive decodes its whole block to reach ANY member, so N batches
     * would decode it N times. One batch → keep the fast path; more than one →
     * the per-entry path, whose cost is at least linear rather than quadratic. */
    if (this._solid && batches.length > 1) {
      for (var j = 0; j < paths.length; j++) {
        await onEntry({ path: paths[j], bytes: await this.extractEntry(paths[j]) });
      }
      return { missing: missing };
    }

    for (var b = 0; b < batches.length; b++) {
      // Whole-archive shorthand when the entire selection is one batch and it
      // IS the archive — lets 7-Zip skip building a listfile it does not need.
      var whole = batches.length === 1 && batches[b].length === Object.keys(sizes).length;
      await this._rpc('extractSolidBegin', {
        password: this._password,
        paths: whole ? null : batches[b].map(function (p) { return self._keyOf(p); }),
      });
      await drain(batches[b]);
    }
    return { missing: missing };
  };

  Engine.prototype.setPassword = function (pw) { this._password = pw || ''; };
  Engine.prototype.setCodepage = function (cp) { this._codepage = cp || 0; };

  /* Terminate first, ask questions never.
   *
   * This used to `await this._rpc('close')` before terminating — an RPC to a
   * worker that is, by definition, quite possibly wedged. 7-Zip runs inside a
   * SYNCHRONOUS callMain(), so while it works the worker cannot service its
   * message queue at all: the 'close' message is queued behind an operation
   * that may run for minutes, and the promise never settles. resetAll() awaits
   * close(), so «Новый архив» — the one escape hatch on the page — was itself
   * dead exactly when the user needed it (telemetry: silent_click on
   * `.arx-reset`, and the reporter of comment #1345 hit precisely this).
   *
   * terminate() frees the whole worker including its MEMFS, so the RPC bought
   * nothing that terminate does not already do. */
  Engine.prototype.close = function () {
    var w = this._worker;
    this._worker = null;
    this._opened = false;
    this._pending.forEach(function (p) {
      var e = new Error('cancelled'); e.code = 'CANCELLED';
      try { p.reject(e); } catch (x) {}
    });
    this._pending.clear();
    if (this._watchdog) { try { this._watchdog.dispose(); } catch (x) {} }
    if (w) { try { w.terminate(); } catch (x) {} }
    return Promise.resolve();
  };

  window.ArchiveEngine = {
    create: function () { return new Engine(); },
    isArchiveName: isArchiveName,
    magicIsArchive: magicIsArchive,
    decodeName: decodeName,
    nameEntries: nameEntries,
    nameReader: nameReader,
    keyBytes: keyBytes,
    ARCHIVE_EXTS: ARCHIVE_EXTS,
    CODEPAGES: CODEPAGES,
    ext: ext,
    volumeInfo: volumeInfo,
    volumeSet: volumeSet,
  };
})();
